import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { isDefined } from '@fi-shared/utils';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { AppTextService } from '@fi-shared/app-text';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';

@Component({
    selector: 'fi-countries-select',
    template: `
        <fi-select-search
          [items]="countries"
          [(ngModel)]="countryId"
          (ngModelChange)="onCountryIdChanged($event)"
          [label]="label"
          [displayValue]="dataTextField"
          [isTextFieldLabelType]="isTextFieldLabelType()"
          [matOptionValueProperty]="dataValueField"
          [appearance]="appearance"
          [customMatOptionNameTemplate]="customMatOptionNameTemplate"
          [filterBy]="filterBy"
          [required]="isRequired"
          [errorText]="errorText"
          [isCustomMatSelectTrigger]="true"
          [disabled]="isDisabled"
          >
          <div class="mat-select-trigger-content">
            @if (isDefinedSelectedCountryData()) {
              <div class="mat-select-trigger">
                @if (!isCountryCodeNewLayout) {
                  <div
                    class="country-flag"
                    [style.background-image]="getFlagIconUrl(selectedCountryData.IsoTwoCode)"
                  ></div>
                  <span class="country-code">{{ selectedCountryData.CountryCode }}</span>
                }
                @if (isCountryCodeNewLayout) {
                  <img [src]="getFlagIconUrlPath(selectedCountryData.IsoTwoCode)" alt="flag" />
                }
              </div>
            }
          </div>
        </fi-select-search>
        
        <ng-template #customMatOptionNameTemplate let-item="item">
          <div class="option-icon" [style.background-image]="getFlagIconUrl(item.IsoTwoCode)"></div>
          <span class="option-name">{{ getCountryLabel(item) }} {{ item.CountryCode }}</span>
        </ng-template>
        `,
    styleUrls: ['./fi-countries-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-countries-select' },
    providers: [
        { provide: FiComponentsContainer, useExisting: FiCountriesSelectComponent },
        { provide: MAT_SELECT_CONFIG, useValue: { overlayPanelClass: 'width-unset' } },
    ],
    standalone: false,
})
export class FiCountriesSelectComponent extends FiComponentsContainer implements OnChanges {
    @Input() countries: ICountryModel[];
    @Input() countryId: number;
    @Input() isDisabled: boolean;
    @Input() isRequired: boolean;
    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() isCountryCodeDisplayed: boolean = true;
    @Input() isCountryCodeNewLayout: boolean;
    @Input() errorText: string;
    @Input() label = this.appTextService.get('address.country');
    @Input() isFormFieldNewDefaultStyle: boolean;
    @Output() countryIdChange = new EventEmitter<number>();

    @Input() filterBy = 'TextId';
    @Input() orderBy = 'TextId';
    @Input() dataValueField = 'Id';
    @Input() dataTextField = 'TextId'; // 'Name';

    selectedCountryData: ICountryModel;

    constructor(
        private appTextService: AppTextService,
        private fiCountryHelperService: FiCountryHelperService,
    ) {
        super();
    }

    isTextFieldLabelType(): boolean {
        return this.dataTextField === 'TextId';
    }

    getCountryLabel(country: ICountryModel) {
        return this.isTextFieldLabelType()
            ? this.appTextService.get(country[this.dataTextField])
            : country[this.dataTextField];
    }

    isDefinedSelectedCountryData(): boolean {
        return isDefined(this.selectedCountryData);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.countries || changes.countryId) this.setCurrentCountry();
    }

    setCurrentCountry() {
        this.selectedCountryData = this.countries.find((dataItem: ICountryModel) => {
            return dataItem[this.dataValueField] === this.countryId;
        });
    }

    getFlagIconUrl(IsoTwoCode: string): string {
        return this.fiCountryHelperService.getCountryFlagUrl(IsoTwoCode);
    }

    getFlagIconUrlPath(IsoTwoCode: string): string {
        return this.fiCountryHelperService.getCountryFlagUrlPath(IsoTwoCode);
    }

    onCountryIdChanged($event: number) {
        this.setCurrentCountry();
        this.countryIdChange.emit($event);
    }
}
